import {
  componentsQuery,
  previewTemplateContent,
  templateQuery,
  useCreateTemplateMutation,
  useSendSpamTestEmailMutation,
  useUpdateTemplateMutation,
  variablesQuery,
} from "@/api/campaigns";
import { Button } from "@/components/ui/Button";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { seo } from "@/utils/seo";
import { Box, ErrorMessage, Field, Flex, Label, Text, toast } from "@givenwell/components";
import { Input } from "@/components/ui/Input";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const Route = createFileRoute("/campaigns/templates/$templateId")({
  component: TemplateEditor,
  head: () => ({ meta: seo({ title: "Edit Template" }) }),
});

interface Component {
  name: string;
  description: string;
  syntax: string;
  example: string;
}

function TemplateEditor() {
  const params = useParams({ strict: false });
  const isNew = !params.templateId || params.templateId === "new";
  const navigate = useNavigate();

  const templateId = isNew ? "" : params.templateId!;
  const query = useQuery({ ...templateQuery(templateId), enabled: !isNew });
  const componentsQueryResult = useQuery(componentsQuery());
  const variablesQueryResult = useQuery(variablesQuery());

  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [markdown_body, setMarkdownBody] = useState("");
  const [theme, setTheme] = useState<string>("branded");
  const [emailType, setEmailType] = useState<"campaign" | "transactional">("campaign");
  const [trackOpens, setTrackOpens] = useState(true);
  const [previewHtml, setPreviewHtml] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [cursorPosition, setCursorPosition] = useState(0);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previewTimeoutRef = useRef<number | null>(null);

  const createMut = useCreateTemplateMutation();
  const updateMut = useUpdateTemplateMutation(templateId);

  useEffect(() => {
    if (query.data) {
      setName(query.data.name);
      setSubject(query.data.subject);
      setMarkdownBody(query.data.markdown_body);
      setTheme(query.data.theme || "branded");
      setEmailType(query.data.email_type || "campaign");
      setTrackOpens(query.data.track_opens ?? true);
    }
  }, [query.data]);

  // Build sample variables from the resolver metadata for preview
  const sampleVariables = useMemo(
    () => Object.fromEntries((variablesQueryResult.data ?? []).map(v => [v.key, v.sample_value])),
    [variablesQueryResult.data],
  );

  // Auto-preview with debouncing
  useEffect(() => {
    // Clear existing timeout
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }

    // Don't preview if body is empty
    if (!markdown_body.trim()) {
      setPreviewHtml("");
      return;
    }

    // Set loading state immediately
    setIsPreviewLoading(true);

    // Debounce preview for 500ms
    previewTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await previewTemplateContent(markdown_body, theme, emailType, sampleVariables);
        setPreviewHtml(result.html);
      } catch (error) {
        console.error("Preview failed:", error);
        setPreviewHtml("<p style='color: red;'>Preview failed. Check your template syntax.</p>");
      } finally {
        setIsPreviewLoading(false);
      }
    }, 500);

    // Cleanup
    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
    };
  }, [markdown_body, theme, emailType, sampleVariables]);

  const handlePreview = useCallback(async () => {
    if (!markdown_body.trim()) {
      setPreviewHtml("<p>Enter some content to preview.</p>");
      return;
    }

    setIsPreviewLoading(true);
    try {
      const result = await previewTemplateContent(markdown_body, theme, emailType, sampleVariables);
      setPreviewHtml(result.html);
    } catch (error) {
      console.error("Preview failed:", error);
      setPreviewHtml("<p style='color: red;'>Preview failed. Check your template syntax.</p>");
    } finally {
      setIsPreviewLoading(false);
    }
  }, [markdown_body, theme, emailType, sampleVariables]);

  function validate() {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = "Name is required";
    if (!subject.trim()) newErrors.subject = "Subject is required";
    if (!markdown_body.trim()) newErrors.markdown_body = "Body is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSave() {
    if (!validate()) return;

    const body = { name, subject, markdown_body, theme, email_type: emailType, track_opens: trackOpens };
    if (isNew) {
      createMut.mutate(body, {
        onSuccess: data => {
          toast.success("Template created");
          navigate({
            to: "/campaigns/templates/$templateId",
            params: { templateId: data.email_template_id },
          });
        },
      });
    } else {
      updateMut.mutate(body, {
        onSuccess: () => toast.success("Template saved"),
      });
    }
  }

  const insertText = (text: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = markdown_body.substring(0, start);
    const after = markdown_body.substring(end);
    const newBody = before + text + after;
    setMarkdownBody(newBody);

    // Set cursor position after the inserted text
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + text.length;
      textarea.setSelectionRange(newPosition, newPosition);
      setCursorPosition(newPosition);
    }, 0);
  };

  const insertComponent = (component: Component) => {
    insertText("\n\n" + component.example + "\n\n");
  };

  const insertVariable = (variable: string) => {
    insertText(`{{${variable}}}`);
  };

  const isPending = createMut.isPending || updateMut.isPending;

  return (
    <PageWrapper
      title={isNew ? "New Template" : "Edit Template"}
      rightContent={
        <Flex css={{ gap: 8 }}>
          <Button variant="plain" color="neutral" onClick={() => navigate({ to: "/campaigns/templates" })}>
            Cancel
          </Button>
          <Button variant="tonal" color="primary" onClick={handleSave} loading={isPending} loadingText="Saving...">
            {isNew ? "Create" : "Save"}
          </Button>
        </Flex>
      }
    >
      <Flex css={{ gap: 16, items: "start" }}>
        {/* Left Sidebar - Components */}
        <Box css={{ flex: "0 0 250px", spaceY: 16 }}>
          <Box css={{ background: "white", p: 16, rounded: 12, spaceY: 12 }}>
            <Text css={{ fontWeight: 600, fontSize: 14 }}>Insert Component</Text>
            <Flex css={{ flexDirection: "column", gap: 8 }}>
              {componentsQueryResult.isLoading ? (
                <Text css={{ fontSize: 12, color: colors.text2 }}>Loading components...</Text>
              ) : componentsQueryResult.error ? (
                <Text css={{ fontSize: 12, color: "red" }}>Error loading components</Text>
              ) : (
                componentsQueryResult.data?.map((comp: Component) => (
                  <Box
                    key={comp.name}
                    css={{
                      border: `1px solid ${colors.outline}`,
                      rounded: 8,
                      p: 8,
                      spaceY: 4,
                    }}
                  >
                    <Button
                      variant="plain"
                      color="primary"
                      size="sm"
                      onClick={() => insertComponent(comp)}
                      style={{ width: "100%", justifyContent: "flex-start" }}
                    >
                      {comp.name}
                    </Button>
                    <Text css={{ fontSize: 12, color: colors.text2 }}>{comp.description}</Text>
                  </Box>
                )) || <Text css={{ fontSize: 12, color: colors.text2 }}>Loading components...</Text>
              )}
            </Flex>
          </Box>

          <Box css={{ background: "white", p: 16, rounded: 12, spaceY: 12 }}>
            <Text css={{ fontWeight: 600, fontSize: 14 }}>Variables</Text>
            <Flex css={{ flexDirection: "column", gap: 4 }}>
              {variablesQueryResult.isLoading ? (
                <Text css={{ fontSize: 12, color: colors.text2 }}>Loading variables...</Text>
              ) : variablesQueryResult.error ? (
                <Text css={{ fontSize: 12, color: "red" }}>Error: {String(variablesQueryResult.error)}</Text>
              ) : variablesQueryResult.data && variablesQueryResult.data.length > 0 ? (
                variablesQueryResult.data.map(v => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => insertVariable(v.key)}
                    style={{
                      padding: "4px 8px",
                      fontSize: 12,
                      textAlign: "left",
                      borderRadius: 4,
                      border: `1px solid ${colors.outline}`,
                      background: colors.cream,
                      cursor: "pointer",
                      fontFamily: "monospace",
                    }}
                  >
                    {`{{${v.key}}}`}
                  </button>
                ))
              ) : (
                <Text css={{ fontSize: 12, color: colors.text2 }}>No variables available</Text>
              )}
            </Flex>
          </Box>
        </Box>

        {/* Main Editor */}
        <Box css={{ flex: "2 0 0px", spaceY: 16, background: "white", p: 16, rounded: 12 }}>
          <Field>
            <Label>Name</Label>
            <Input value={name} onValueChange={setName} placeholder="e.g. Monthly newsletter" />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </Field>
          <Field>
            <Label>Subject</Label>
            <Input
              value={subject}
              onValueChange={setSubject}
              placeholder="e.g. Your {{supporter_name}} update for this month"
            />
            {errors.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
          </Field>
          <Field>
            <Label>Theme</Label>
            <select
              value={theme}
              onChange={e => setTheme(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                fontSize: 14,
                borderRadius: 8,
                border: `1px solid ${colors.outline}`,
                background: "white",
              }}
            >
              <option value="branded">Branded (with logo & styled card)</option>
              <option value="plain">Plain Text (simple & clean)</option>
              <option value="minimal">Minimal (lightweight styling)</option>
            </select>
            <Text css={{ fontSize: 12, color: colors.text2, mt: 4 }}>Choose how your email will be styled</Text>
          </Field>
          <Field>
            <Label>Email Type</Label>
            <select
              value={emailType}
              onChange={e => setEmailType(e.target.value as "campaign" | "transactional")}
              style={{
                width: "100%",
                padding: "8px 12px",
                fontSize: 14,
                borderRadius: 8,
                border: `1px solid ${colors.outline}`,
                background: "white",
              }}
            >
              <option value="campaign">Campaign</option>
              <option value="transactional">Transactional</option>
            </select>
            <Text css={{ fontSize: 12, color: colors.text2, mt: 4 }}>
              {emailType === "campaign"
                ? "Sent from the campaign address and includes an unsubscribe footer."
                : "Sent from the main address with no unsubscribe footer. Use for receipts, notifications, and account emails."}
            </Text>
          </Field>
          <Field>
            <Label>Track Opens</Label>
            <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
              <input type="checkbox" checked={trackOpens} onChange={e => setTrackOpens(e.target.checked)} />
              <Text css={{ fontSize: 14 }}>Enable open tracking</Text>
            </label>
            <Text css={{ fontSize: 12, color: colors.text2, mt: 4 }}>
              When enabled, a tracking pixel is embedded to detect when recipients open the email.
            </Text>
          </Field>
          <Field>
            <Label>Body (Markdown + Components)</Label>
            <Text css={{ fontSize: 12, color: colors.text2, mb: 8 }}>
              Write in Markdown or use the components in the sidebar to add buttons, images, and more.
            </Text>
            <textarea
              ref={textareaRef}
              value={markdown_body}
              onChange={e => setMarkdownBody(e.target.value)}
              onSelect={e => setCursorPosition(e.currentTarget.selectionStart)}
              rows={20}
              style={{
                width: "100%",
                fontFamily: "monospace",
                fontSize: 14,
                padding: 12,
                borderRadius: 8,
                border: `1px solid ${errors.markdown_body ? "red" : colors.outline}`,
                resize: "vertical",
              }}
              placeholder={`Hi {{given_name}},

We're excited to share some updates from {{supporter_name}}!

## What's New

- Enhanced wellness benefits
- Expanded provider network
- Personalized recommendations

<Button href="https://app.givenwell.com/marketplace">
  Explore Your Options
</Button>

Questions? Just reply to this email.`}
            />
            {errors.markdown_body && <ErrorMessage>{errors.markdown_body}</ErrorMessage>}
          </Field>
        </Box>

        {/* Right Sidebar - Preview */}
        <Box css={{ flex: "1 0 0px", spaceY: 16 }}>
          <Box css={{ background: "white", p: 16, rounded: 12, spaceY: 12 }}>
            <Flex css={{ justify: "between", items: "center" }}>
              <Text css={{ fontWeight: 600 }}>
                Live Preview
                {isPreviewLoading && <span style={{ marginLeft: 8, fontSize: 12, color: colors.text2 }}>⟳</span>}
              </Text>
              <Button
                variant="plain"
                color="primary"
                size="sm"
                onClick={() => setShowFullPreview(true)}
                disabled={!previewHtml}
              >
                Open Full Preview
              </Button>
            </Flex>
            {previewHtml ? (
              <iframe
                srcDoc={previewHtml}
                sandbox=""
                style={{
                  width: "100%",
                  height: 500,
                  border: `1px solid ${colors.outline}`,
                  borderRadius: 8,
                  background: "white",
                  opacity: isPreviewLoading ? 0.6 : 1,
                  transition: "opacity 0.2s",
                }}
              />
            ) : (
              <Box
                css={{
                  height: 500,
                  border: `1px solid ${colors.outline}`,
                  rounded: 8,
                  display: "flex",
                  items: "center",
                  justify: "center",
                  color: colors.text2,
                }}
              >
                <Text css={{ fontSize: 14 }}>Start typing to see a live preview...</Text>
              </Box>
            )}
          </Box>

          <SpamTestPanel templateId={templateId} isNew={isNew} trackOpens={trackOpens} />
        </Box>
      </Flex>

      {/* Full Preview Dialog */}
      {showFullPreview && previewHtml && (
        <Box
          css={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 24,
          }}
          onClick={() => setShowFullPreview(false)}
        >
          <Box
            css={{
              background: "white",
              rounded: 12,
              maxWidth: "900px",
              width: "100%",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
            onClick={e => e.stopPropagation()}
          >
            <Flex css={{ justify: "between", items: "center", p: 16, borderBottom: `1px solid ${colors.outline}` }}>
              <Text css={{ fontWeight: 600, fontSize: 16 }}>Full Email Preview</Text>
              <Button variant="plain" color="neutral" onClick={() => setShowFullPreview(false)}>
                Close
              </Button>
            </Flex>
            <Box css={{ flex: 1, overflow: "auto", background: "#f5f5f5", p: 16 }}>
              <iframe
                srcDoc={previewHtml}
                sandbox=""
                style={{
                  width: "100%",
                  height: "100%",
                  minHeight: "100vh",
                  border: "none",
                  display: "block",
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </PageWrapper>
  );
}

function SpamTestPanel({ templateId, isNew, trackOpens }: { templateId: string; isNew: boolean; trackOpens: boolean }) {
  const [toAddress, setToAddress] = useState("");
  const sendSpamTest = useSendSpamTestEmailMutation(templateId);

  function handleSend() {
    if (!toAddress.trim()) return;
    sendSpamTest.mutate(
      { toAddress: toAddress.trim(), trackOpens },
      {
        onSuccess: () => toast.success("Spam test email sent"),
      },
    );
  }

  return (
    <Box css={{ background: "white", p: 16, rounded: 12, spaceY: 12 }}>
      <Text css={{ fontWeight: 600, fontSize: 14 }}>Spam Test</Text>
      <Text css={{ fontSize: 12, color: colors.text2 }}>
        Get a test address from{" "}
        <a
          href="https://app.mailgenius.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: colors.brand500 }}
        >
          MailGenius
        </a>
        , paste it below, and we'll send the rendered template to it.
      </Text>
      <Field>
        <Input
          placeholder="test-abc123@test.mailgenius.com"
          value={toAddress}
          onChange={e => setToAddress(e.target.value)}
          disabled={isNew || sendSpamTest.isPending}
        />
      </Field>
      <Button
        variant="tonal"
        color="primary"
        size="sm"
        onClick={handleSend}
        disabled={isNew || !toAddress.trim() || sendSpamTest.isPending}
        loading={sendSpamTest.isPending}
        loadingText="Sending..."
        title={isNew ? "Save template first" : undefined}
        style={{ width: "100%" }}
      >
        Send Test Email
      </Button>
    </Box>
  );
}
